export interface DeviceInfo {
  os: "windows" | "macos" | "linux" | "android" | "ios" | "unknown";
  browser: "chrome" | "firefox" | "safari" | "edge" | "opera" | "unknown";
  isMobile: boolean;
  isPWASupported: boolean;
  isStandalone: boolean;
}

export const detectDevice = (): DeviceInfo => {
  const userAgent = navigator.userAgent.toLowerCase();
  const platform = navigator.platform?.toLowerCase() || "";

  // Detect OS
  let os: DeviceInfo["os"] = "unknown";
  if (userAgent.includes("win")) os = "windows";
  else if (userAgent.includes("mac") && !userAgent.includes("iphone") && !userAgent.includes("ipad")) os = "macos";
  else if (userAgent.includes("linux") && !userAgent.includes("android")) os = "linux";
  else if (userAgent.includes("android")) os = "android";
  else if (userAgent.includes("iphone") || userAgent.includes("ipad") || userAgent.includes("ipod")) os = "ios";

  // Detect Browser
  let browser: DeviceInfo["browser"] = "unknown";
  if (userAgent.includes("edg/")) browser = "edge";
  else if (userAgent.includes("opr/") || userAgent.includes("opera")) browser = "opera";
  else if (userAgent.includes("chrome") && !userAgent.includes("edg/")) browser = "chrome";
  else if (userAgent.includes("firefox")) browser = "firefox";
  else if (userAgent.includes("safari") && !userAgent.includes("chrome")) browser = "safari";

  // Detect if mobile
  const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);

  // Check if PWA is supported
  const isPWASupported = "serviceWorker" in navigator && "PushManager" in window;

  // Check if running as standalone PWA
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true;

  return {
    os,
    browser,
    isMobile,
    isPWASupported,
    isStandalone,
  };
};

export const getShortcutInstructions = (device: DeviceInfo): string => {
  if (device.isStandalone) {
    return "Você já está usando o app instalado!";
  }

  const browserInstructions: Record<string, Record<string, string>> = {
    chrome: {
      windows: "Clique no ícone ⋮ no canto superior direito → Mais ferramentas → Criar atalho",
      macos: "Clique no ícone ⋮ no canto superior direito → Mais ferramentas → Criar atalho",
      linux: "Clique no ícone ⋮ no canto superior direito → Mais ferramentas → Criar atalho",
      android: "Toque no ícone ⋮ → Adicionar à tela inicial",
      ios: "Use o Safari para adicionar à tela inicial",
      unknown: "Clique no menu do navegador → Criar atalho ou Adicionar à tela inicial",
    },
    firefox: {
      windows: "Arraste o ícone do cadeado na barra de endereço para a área de trabalho",
      macos: "Arraste o ícone do cadeado na barra de endereço para o Dock",
      linux: "Arraste o ícone do cadeado na barra de endereço para a área de trabalho",
      android: "Toque no ícone ⋮ → Instalar",
      ios: "Use o Safari para adicionar à tela inicial",
      unknown: "Arraste o ícone do cadeado para a área de trabalho",
    },
    safari: {
      macos: "Arraste o ícone de URL na barra de endereço para a área de trabalho ou Dock",
      ios: "Toque no ícone de compartilhar ↗ → Adicionar à Tela de Início",
      unknown: "Use o menu Arquivo → Adicionar aos Favoritos ou arraste para a área de trabalho",
    },
    edge: {
      windows: "Clique no ícone ⋯ no canto superior direito → Aplicativos → Instalar este site como um aplicativo",
      macos: "Clique no ícone ⋯ no canto superior direito → Aplicativos → Instalar este site",
      linux: "Clique no ícone ⋯ no canto superior direito → Aplicativos → Instalar",
      unknown: "Clique no menu → Aplicativos → Instalar este site",
    },
    opera: {
      windows: "Clique no ícone do menu → Mais ferramentas → Criar atalho",
      macos: "Clique no ícone do menu → Mais ferramentas → Criar atalho",
      linux: "Clique no ícone do menu → Mais ferramentas → Criar atalho",
      unknown: "Use o menu do navegador para criar um atalho",
    },
    unknown: {
      unknown: "Consulte as configurações do seu navegador para criar um atalho na área de trabalho",
    },
  };

  const browserName = browserInstructions[device.browser] ? device.browser : "unknown";
  const osName = browserInstructions[browserName][device.os] ? device.os : "unknown";

  return browserInstructions[browserName][osName] || browserInstructions.unknown.unknown;
};

export const getBrowserName = (browser: DeviceInfo["browser"]): string => {
  const names: Record<string, string> = {
    chrome: "Chrome",
    firefox: "Firefox",
    safari: "Safari",
    edge: "Edge",
    opera: "Opera",
    unknown: "seu navegador",
  };
  return names[browser] || "seu navegador";
};

export const getOSName = (os: DeviceInfo["os"]): string => {
  const names: Record<string, string> = {
    windows: "Windows",
    macos: "macOS",
    linux: "Linux",
    android: "Android",
    ios: "iOS",
    unknown: "seu sistema",
  };
  return names[os] || "seu sistema";
};
